import { useContext, useEffect, useState } from 'react'
import {
  returnsAnalytics,
  returnsClickEvent
} from '@components/returns/helpers'
import { Values, ReasonListItemField } from '@components/returns/types'
import { ReasonStep, AdditionalFieldsStep } from './RefundSteps'
import { PartialState, ValuesState } from '../index'
import ReturnsContext from '../../context'
import { ReturnReason, ReturnsProduct } from '@api/returns/types'
import { FormProvider, useForm } from 'react-hook-form'

function getRemainingSteps(
  containsAdditionalReasonFields?: ReasonListItemField[]
) {
  if (containsAdditionalReasonFields) {
    return [AdditionalFieldsStep]
  }
  return []
}

type RefundFormProps = {
  selectedProduct: ReturnsProduct
  onDone: () => void
  setProductToExchange: ({ step }: { step: number }) => void
  setValues: (partialState: PartialState<ValuesState>) => void
  values: Values
  reasons: ReturnReason[]
}

const RefundForm = ({
  selectedProduct,
  values,
  setValues,
  onDone,
  reasons,
  setProductToExchange
}: RefundFormProps) => {
  const { setStoredSavedFit } = useContext(ReturnsContext)
  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      reason: ''
    }
  })

  const reasonList = reasons.map(reason => ({
    label: reason.title,
    value: reason.id,
    fields: reason.fields,
    available: true
  }))
  const containsAdditionalReasonFields: ReasonListItemField[] | undefined =
    reasonList.find(reason => reason.value === values.reason)?.fields
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const remainingSteps = getRemainingSteps(containsAdditionalReasonFields)
  const steps = [ReasonStep, ...remainingSteps]
  const isLastStep = activeStepIndex === steps.length - 1

  const CurrentStepComponent = steps[activeStepIndex]

  const setSelection = (selection: PartialState<ValuesState>) => {
    const { reason, sizes } = selection || {}

    if (reason) {
      const returnReasonLabel =
        (
          reasonList.find(
            reasonListItem => reasonListItem.value === Number(reason)
          ) || {}
        ).label || ''

      returnsAnalytics('Return Reason Selected', {
        color: selectedProduct.title,
        name: selectedProduct.product_type,
        product_id: selectedProduct.product_id,
        return_type: values.method,
        return_reason: returnReasonLabel
      })
      setValues({
        reason: Number(reason),
        reason_detail: []
      })
    }
    if (sizes) {
      setStoredSavedFit(sizes)
    }
    setValues(selection)
  }

  const handleUpdate = (step = 'next') => {
    if (isLastStep && step === 'next') {
      returnsClickEvent({
        label: 'Done'
      })
      onDone()
    } else {
      setActiveStepIndex(currentActiveStepIndex =>
        step === 'next'
          ? currentActiveStepIndex + 1
          : currentActiveStepIndex - 1
      )
    }
  }

  useEffect(() => {
    const subscription = methods.watch(values => {
      setSelection({ ...values, reason: Number(values.reason) })
    })

    return () => subscription.unsubscribe()
  }, [methods])

  return (
    <FormProvider {...methods}>
      <CurrentStepComponent
        selectedProduct={selectedProduct}
        reasonList={reasonList}
        setSelection={setSelection}
        values={values}
        isLastStep={isLastStep}
        onUpdate={handleUpdate}
        onExchange={setProductToExchange}
      />
    </FormProvider>
  )
}

export default RefundForm
